
import Vue from "vue";
import {TicketMessage} from "@/models/ticket-message";
import {TicketService} from "@/services/ticket-service";
import AppAttachement from "./TicketMessageAttachement.vue";
import AppAttentions from "./TicketMessageAttentions.vue";
import {TokenStore} from "@/utils/token-store";
import {autoLink} from "@/utils/auto-link";
import {specialLink} from "@/utils/special-link";
import { ISelectItem } from "winfakt-vue-components";
import { TicketMessageAttention } from "@/models/ticket-message-attention";
import AppMessageLikeButton from "./TicketMessageLikeButton.vue";
import { Ticket } from "@/models/ticket";
import { copyToClipboard } from "@/utils/clipboard";

export default Vue.extend({
	data() {
		return {
			updatedContent: "",
			showRemoveDepartementAttentions: false
		};
	},
	props: {
		ticket: {
			type: Object as ()=>Ticket,
			required: true
		},
		value: {
			type: Object as () => TicketMessage,
			required: true
		},
		title: {
			type: String,
			required: false
		},
		updating: {
			type: Boolean
		}

	},
	computed: {
		ticketMessageDropDownMenuTooltip(): string{
			return "";
		},
		/**
		 * Action list shown when clicking on the options menu
		 */
		actionList():ISelectItem<string>[] {
			let data = [
				{text: "Wis antwoord", value: "deleteSelectedTicketMessage", icon: "times-circle"},
				{text: "Bewerk antwoord", value: "startUpdatingMessage", icon: "pen"},
				{text: "Attenties bewerken", value: "startUpdatingAttentions", icon: "exclamation"},
			];
			if (this.value.isInternal) {
				data.push({
					text: "Maak extern", value: "toggleTicketMessageVisibliltyStatus", icon: "eye"
				});
			} else {
				data.push({
					text: "Maak intern", value: "toggleTicketMessageVisibliltyStatus", icon: "eye-slash"
				});
			}
			if(!this.isFromCurrentUser){
				data = [];
			}
			data.push(
				{
					text: (this.value.isLiked() ? "Niet meer liken" : "Thumbs up!"), value: "toggleLike", icon: (this.value.isLiked() ? "thumbs-down" : "thumbs-up")
				},
				{
					text: "Link kopiëren", value: "copyMessageLink", icon: "copy"
				}
			);
			return data;
		},
		content(): string {
			return specialLink(autoLink((this.value.content)));
		},
		locale(): string {
			return "nl-be";
		},
		profileImage(): string {
			let i = this.value.getProfileImageUrl();
			return i ? `url(${i})` : "";
		},
		isFromOwner(): boolean {
			return this.value.adminId == 0;
		},
		isFromCurrentUser(): boolean {
			if (this.value.admin) {
				if (this.value.admin.email == TokenStore.email) {
					return true;
				}
			}
			return false;
		},
		hasAttentions(): boolean {
			return this.value.attentions.length > 0;
		},
		currentUserHasAttention(): boolean {
			for (let attention of this.value.attentions) {
				if (!attention.admin) {
					continue;
				}
				if (attention.admin.email == TokenStore.email && !attention.read) {
					return true;
				}
			}
			return false;
		},
		attentionNames(): string {
			return this.value.attentions.map(v => v.admin ? v.admin.name : "").join(", ");
		},
		markDepartementAsReadToolTip(): string{
			return "Alle attenties aanduiden van jouw afdeling";
		},
		removeDepartementAttentionsText(): string {
			return "Ben je zeker dat je alle attenties van jouw afdeling wil verwijderen? \n Vergeet niet te reageren.";
		},
		hasMultipleAttentions(): boolean{
			let atts = [];
			for (let index = 0; index < this.ticket.messages.length; index++) {
				const msg = this.ticket.messages[index];
				for (let index = 0; index < msg.attentions.length; index++) {
					const att = msg.attentions[index];
					if (!att.read && att.adminId == TokenStore.userId) {
						atts.push(att.id);
						break;
					}
				}
			}
			if (atts.length >= 2) {
				return true;
			}
			return false;
		}
	},
	watch: {
		async "updating"() {
			if (!this.updating) return;
			this.updatedContent = this.value.content;
			await this.$nextTick();
		},
	},
	methods: {
		replyClicked(){
			this.$emit("reply-to", this.value.getFirstName());
		},
		timestamp(val: Date): string {
			let days = ["Zo", "Ma", "Di", "Woe", "Do", "Vr", "Za"];
			return days[val.getDay()] + ` ${val.getDate()}/${val.getMonth() + 1}/${val.getFullYear()} ${("0" + val.getHours()).slice(-2)}:${("0" + val.getMinutes()).slice(-2)}`;
		},
		async markDepartementAsRead() {
			let result = await TicketService.markDepartmentAttentionAsRead(this.value.ticketId, this.value.id);
			if (result) {
				this.value.attentions = result.attentions;
			}
			this.showRemoveDepartementAttentions = false;
			this.$emit("refresh");
			this.$emit("setFocusToTextArea");
		},
		async deleteSelectedTicketMessage() {
			let confirm = await this.$wf.confirm("Are you sure you want to delete this message?", "yes", "no", "Warning!");
			if (confirm) {
				await TicketService.deleteSelectedTicketMessage(this.value.ticketId, this.value.id);
			}
			return confirm;
		},
		async startUpdatingMessage() {
			this.$emit("updatingChange", true);
		},
		async startUpdatingAttentions() {
			this.$emit("updateAttentions", this.value);
		},
		async stopUpdatingMessage() {
			this.$emit("updatingChange", false);
		},
		async updateMessage() {
			if (this.content === this.updatedContent) {
				this.stopUpdatingMessage();
				return;
			}
			requestAnimationFrame(async () => {
				if (/^\s*$/.exec(this.updatedContent)) {
					if (!this.deleteSelectedTicketMessage()) {
						this.stopUpdatingMessage();
					}
					return;
				}
				await TicketService.updateTicketMessage(
					this.value.ticketId,
					this.value.id,
					this.updatedContent);
				this.stopUpdatingMessage();
			});
		},
		/**
		 * Trigger selected action from actionlist
		 */
		triggerAction(value: string) {
			(this as any)[value]();
		},
		async toggleTicketMessageVisibliltyStatus(){
			await TicketService.toggleTicketMessageInternal(this.value.ticketId, this.value.id);
		},
		async toggleAsRead(attention: TicketMessageAttention) {
			let result = undefined;
			if (attention.read) {
				result = await TicketService.markAttentionAsUnRead(this.value.ticketId, this.value.id, attention.id);
			}
			if (!attention.read) {
				if (this.hasMultipleAttentions) {
					let confirm = await this.$wf.confirm("Zal ik alle attenties binnen dit ticket afvinken?", "Ja", "Neen", "Vraagje?");
					if (confirm) {
						let response = await TicketService.markAllAttentionsOfUserAsRead(this.value.ticketId);
						if (response && response.messages.length > 0 ) {
							this.ticket.messages = response.messages;
						}
					} else {
						result = await TicketService.markAttentionAsRead(this.value.ticketId, this.value.id, attention.id);
					}
				} else {
					result = await TicketService.markAttentionAsRead(this.value.ticketId, this.value.id, attention.id);
				}
			}

			if (result) {
				this.value.attentions = result.attentions;
			}
		},
		async toggleLike(){
			if (!this.value.isLiked()) {
				await TicketService.like(this.ticket, this.value);
			} else {
				await TicketService.unlike(this.ticket, this.value);
			}
		},
		copyMessageLink(){
			let url = `${location.origin}/ticket/${this.ticket.id}?messageId=${this.value.id}`;
			copyToClipboard(url);
			this.$wf.notify("Link is gekopieerd naar klembord", "success", 3000, "check");
		}
	},
	mounted() {
		this.$forceUpdate();
	},
	components: {
		AppAttachement,
		AppAttentions,
		AppMessageLikeButton
	},
});

